import React, { useEffect, useState, useCallback, useContext } from "react";
import axios from "axios";
import {
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	TextField,
	Box,
	
} from "@mui/material";
import { FaDownload } from "react-icons/fa";
import { FiInbox } from "react-icons/fi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from '../config/api';
import BRANDING from "../config/branding";

const Logs = () => {
	const navigate = useNavigate();
	const { logout } = useContext(AuthContext);

	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");

	const getFiles = useCallback(async () => {
		setLoading(true);
		const token = localStorage.getItem("token");

		if (!token) {
			logout();
			toast.error("No authentication token found. Please log in.");
			setLoading(false);
			return;
		}

		try {
			const { data, status } = await axios.get(
				`${API_URL}/list.php`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			if (status === 401 || status === 403) {
				toast.error(
					data?.message || "Authentication failed. Please log in again."
				);
				logout();
				setLoading(false);
				return;
			}
			if (Array.isArray(data)) {
				setFiles(data);
			} else if (data && data.status && Array.isArray(data.data)) {
				setFiles(data.data);
			} else {
				toast.error(data?.message || "Failed to fetch file list.");
				console.warn("Unexpected response format:", data);
			}
			setLoading(false);
		} catch (e) {
			console.error("Error fetching file list:", e);
			toast.error("An error occurred while fetching files.");
			setLoading(false);

			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status === 401 || e.response.status === 403) {
					logout();
					toast.info("Authentication failed. Please log in again.");
				}
			}
		}
	}, [logout]);

	useEffect(() => {
		getFiles();
	}, [getFiles]);

	const handleDownload = (filename) => {
		const url = `http://localhost:8080/downloadcsv.php?file=${filename}`;
		window.location.href = url;
	};

	const filteredFiles = files.filter((file) =>
		file.toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (loading) {
		return (
			<div className='flex justify-center items-center min-h-[200px] font-inter'>
				<CircularProgress sx={{ color: "#1E2F97" }} />
			</div>
		);
	}

	return (
		<div className='font-inter'>
			<div className='flex justify-center mb-4'>
	<img
		src={BRANDING.logo}
		alt={BRANDING.shortName}
		className='h-16 w-auto object-contain'
	/>
</div>
			<Typography
    variant='h4'
    gutterBottom
    align='center'
    sx={{
        mb: 1,
        fontWeight: 700,
        color: BRANDING.primaryColor,
    }}
>
    File Downloads
</Typography>

<Typography
    variant='body1'
    align='center'
    sx={{
        mb: 4,
        color: "#64748B",
    }}
>
    Browse and download generated communication reports
</Typography>
			<Box
    display='flex'
    justifyContent='center'
    mb={4}
    sx={{
        p: 3,
        backgroundColor: "#F8FAFC",
        borderRadius: 3,
        border: "1px solid #E2E8F0",
    }}
>
				<TextField
					label='Search files...'
					variant='outlined'
					size='small'
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					sx={{
						width: "300px",
						"& .MuiOutlinedInput-root": {
							"& fieldset": { borderColor: "#FECACA" },
"&:hover fieldset": { borderColor: BRANDING.primaryColor },
"&.Mui-focused fieldset": { borderColor: BRANDING.primaryColor },
						},
						"& .MuiInputLabel-root.Mui-focused": {color: BRANDING.primaryColor },
					}}
				/>
			</Box>
			<Typography
				variant="body2"
				sx={{
					mb: 3,
					color: "#64748B",
					textAlign: "center",
					fontWeight: 500,
				}}
			>
				{filteredFiles.length} file(s) available
			</Typography>

			<Grid container spacing={3} justifyContent='center'>
				{filteredFiles.length > 0 ? (
					filteredFiles.map((file, index) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={index}>
							<Card
								variant='outlined'
								sx={{
									borderRadius: "10px",
									boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
									backgroundColor: "white",
									border: "1px solid #E2E8F0",
									"&:hover": {
										borderColor: BRANDING.primaryColor,
										boxShadow: "0 6px 14px rgba(211,47,47,0.15)",
									},
								}}
							>
								<CardContent
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										p: 3,
									}}
								>
									<Typography
										variant='body1'
										gutterBottom
										sx={{
											mb: 2,
											textAlign: "center",
											wordBreak: "break-word",
											color: "#333",
										}}
									>
										{file}
									</Typography>
									<Button
										variant='contained'
										startIcon={<FaDownload />}
										onClick={(e) => {
											e.stopPropagation();
											handleDownload(file);
										}}
										sx={{
											background: `linear-gradient(135deg, ${BRANDING.primaryColor}, #B91C1C)`,
											"&:hover": { background: "#B91C1C" },
											color: "white",
											textTransform: "none",
											borderRadius: "8px",
											padding: "10px 20px",
											fontWeight: "bold",
										}}
										fullWidth
									>
										Download
									</Button>
								</CardContent>
							</Card>
						</Grid>
					))
				) : (
					<Grid item xs={12}>
						<Box textAlign='center' sx={{ py: 8 }}>
    <FiInbox
        size={60}
        color={BRANDING.primaryColor}
        style={{ marginBottom: 16 }}
    />

    <Typography
        variant='h6'
        sx={{
            color: "#1E293B",
            fontWeight: 600,
        }}
    >
        No Files Found
    </Typography>

    <Typography
        variant='body2'
        sx={{
            color: "#64748B",
            mt: 1,
        }}
    >
        Try adjusting your search criteria.
    </Typography>
</Box>
					</Grid>
				)}
			</Grid>
		</div>
	);
};

export default Logs;
